import { observer, useLocalObservable } from "mobx-react-lite";
import React, { useMemo } from "react";
import { ScreenResponsive } from "../../../components";
import {
	Box,
	ButtonLayout,
	Grid,
	GridItem,
	IconUserAccountLight,
	Stack,
	TextField,
	skinVars,
	useScreenSize,
} from "@telefonica/mistica";
import { Button } from "../../../components/atoms";
import { strings } from "../../../localization";
import { useGlobalStore } from "../../../contexts/globalStoreContext";
import { HeroDataInfo } from "../../../components/organisms";
import { cleanString, formatDateDDMMYYYY } from "../../../utils/formatters";
import { Store } from "./store";
import { useAnalytics, useNavigateCustom } from "../../../hooks";

const PersonalAditionalInfo: React.FC = observer(() => {
	const navigate = useNavigateCustom();
	const { authenticationStore, fintechRegisterStore } = useGlobalStore();
	const store = useLocalObservable(() => new Store(
		authenticationStore,
		fintechRegisterStore,
	));
	const { isMobile, isDesktopOrBigger } = useScreenSize();
	const { anticipation } = strings.pages.navigation;
	const {
		title,
		description,
		fields: { birthday, motherName },
	} = strings.pages.personalAditionalInfo;
	const { buttonPrimary } = strings.components.buttons;
	const { updatePageView, addAnalyticEvent } = useAnalytics();

	const handleAnalyticEvent = (): void => {
		addAnalyticEvent({
			component: "button",
			action: "click.button",
			step: "fgts:cadastro:dados-pessoais",
			description: buttonPrimary,
			event: "interaction",
		}, { applySanitize: true });
	};

	React.useEffect(() => {
		updatePageView({
			name: "Vivo Money Cadastro FGTS Dados Pessoais",
			pagePath: "/cadastro-fgts/dados-pessoais",
		});
	}, []);

	const isDisabledButton = useMemo(() => fintechRegisterStore.motherName.field.value === "" || fintechRegisterStore.birthDate.field.value === "", [fintechRegisterStore.birthDate.field.value, fintechRegisterStore.motherName.field.value]);

	return (
		<ScreenResponsive onBack={() => navigate(-1)} navigationTitle={anticipation}>
			<Box paddingBottom={isDesktopOrBigger ? 24 : 0}>
				<Grid columns={isDesktopOrBigger ? 4 : 1} justifyContent={"center"}>
					<GridItem columnStart={2} span={isDesktopOrBigger ? 2 : 1}>
						<Box
							paddingTop={isMobile ? 32 : 40}
							paddingBottom={isDesktopOrBigger ? 80 : 32}
							width={isDesktopOrBigger ? 600 : "100%"}
						>
							<HeroDataInfo
								icon={<IconUserAccountLight size={64} color={skinVars.colors.brand} />}
								title={title}
								description={description}
							/>
							<Grid columns={{ maxSize: 392, minSize: 327 }} justifyContent="center">
								<Box paddingY={32}>
									<Stack space={16}>
										<TextField
											label={birthday.label}
											name={birthday.name}
											maxLength={10}
											placeholder={birthday.placeholder}
											value={fintechRegisterStore.birthDate.field.value}
											disabled={store.isLoading}
											onChangeValue={(t) => fintechRegisterStore.birthDate.onChange(formatDateDDMMYYYY(t))}
											data-cs-mask
										/>
										<TextField
											label={motherName.label}
											name={motherName.name}
											placeholder={motherName.placeholder}
											value={fintechRegisterStore.motherName.field.value}
											disabled={store.isLoading}
											onChangeValue={(t) => fintechRegisterStore.motherName.onChange(cleanString(t))}
											helperText={motherName.helperText}
											data-cs-mask
										/>
									</Stack>
								</Box>
								<Box paddingTop={isDesktopOrBigger ? 8 : 0} paddingBottom={isDesktopOrBigger ? 16 : 0}>
									<ButtonLayout>
										<Button
											textButton={buttonPrimary}
											onAction={() => {
												handleAnalyticEvent();
												store.fetchData(
													() => navigate("/fintech-registration/validate-token-otp-email"),
												);
											}}
											isLoading={store.isLoading}
											isDisabled={store.isSubmitButtonDisabled || isDisabledButton}
										/>
									</ButtonLayout>
								</Box>
							</Grid>
						</Box>
					</GridItem>
				</Grid>
			</Box>
		</ScreenResponsive>
	);
});

export default PersonalAditionalInfo;
